import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Download, Pencil, Factory, Copy, Trash2, Wallet,
  FileText, ShoppingCart, ListChecks, ArrowRight, XCircle, Ban, Info,
  CalendarDays, Send,
} from "lucide-react";
import ProductionPlanDialog from "@/components/ProductionPlanDialog";
import EditOrderSheet from "@/components/EditOrderSheet";
import PaymentDialog from "@/components/PaymentDialog";
import DuplicateOrderDialog from "@/components/DuplicateOrderDialog";
import QuoteLineApprovalDialog from "@/components/QuoteLineApprovalDialog";
import InvoiceOrderDialog from "@/components/InvoiceOrderDialog";
import ProformaDialog from "@/components/ProformaDialog";
import { QuoteCloseDialog, QuoteCloseInfoDialog, InvoiceDatesDialog } from "@/pages/OrdersList";
import { useAuth } from "@/context/AuthContext";
import { canOrder, canProdEdit } from "@/lib/orderPerms";
import { SUPPLY_LABEL, supplySummary } from "@/lib/lineMath";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (n) => Number(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 3 });
const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("tr-TR"); } catch { return "—"; } };
const CUR_SYM = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };
const APPROVAL_LABEL = { bekliyor: "Onay Bekliyor", onaylandi: "Onaylandı", reddedildi: "Reddedildi" };

const SUPPLY_METHODS = [
  ["internal", "İç Üretim", "border-blue-500 bg-blue-950/40 text-blue-300"],
  ["ready_purchase", "Hazır Alım", "border-emerald-500 bg-emerald-950/40 text-emerald-300"],
  ["outsourced", "Fason Üretim", "border-amber-500 bg-amber-950/40 text-amber-300"],
];

const BH_BADGE = {
  sent: ["BH ✓", "bg-emerald-950/30 border-emerald-900/60 text-emerald-300"],
  unverified: ["BH ? Doğrulanamadı", "bg-amber-950/30 border-amber-900/60 text-amber-300"],
  failed: ["BH Hata", "bg-rose-950/30 border-rose-900/60 text-rose-300"],
  sending: ["BH Gönderiliyor", "bg-slate-900 border-slate-700 text-slate-300"],
};

function OrderInvoicesCard({ invoices, onSend }) {
  return (
    <Card className="bg-slate-900 border-slate-800 rounded-sm p-4" data-testid="order-invoices-card">
      <div className="text-xs uppercase font-mono text-slate-500 mb-3">Faturalar ({invoices.length})</div>
      <div className="divide-y divide-slate-800">
        {invoices.map((f) => {
          const b = BH_BADGE[f.bh_send_status];
          return (
            <div key={f.id} className="flex flex-wrap items-center gap-3 py-2" data-testid={`order-invoice-${f.id}`}>
              <Link to={`/satis/${f.id}`} className="font-mono text-sm text-cyan-400 hover:underline" data-testid={`order-invoice-link-${f.id}`}>{f.order_no}</Link>
              <span className="text-xs text-slate-500 font-mono">{(f.invoice_date || "").slice(0, 10)}</span>
              {f.total != null && <span className="text-xs font-mono text-slate-300">{fmt(f.total)} {f.currency}</span>}
              {b && <Badge variant="outline" title={f.bh_ref ? `Bizim Hesap ref: ${f.bh_ref}` : f.bh_error || ""} data-testid={`order-invoice-bh-badge-${f.id}`}
                className={`${b[1]} text-[11px] leading-none py-[5px] px-2.5 font-mono font-semibold`}>{b[0]}</Badge>}
              {!["sent", "sending"].includes(f.bh_send_status) && (
                <Button size="sm" variant="outline" onClick={() => onSend(f)}
                  data-testid={`order-invoice-bh-send-${f.id}`}
                  className="ml-auto rounded-sm text-xs border-indigo-800 text-indigo-400 hover:bg-indigo-950/40">
                  <Send className="w-3.5 h-3.5 mr-1.5" />Bizim Hesap'a Gönder
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function SupplyMethodCard({ order, canEdit, onChanged, onStartProduction, onCreateRequest }) {
  const [saving, setSaving] = useState(false);
  const hasDec = (order.line_items || []).some((l) => l.stock_qty != null);
  const summary = supplySummary(order);
  const decMethods = Object.keys(summary).filter((m) => m !== "stock");
  const selected = hasDec
    ? (decMethods.length > 1 ? "mixed" : decMethods[0] || (summary.stock ? "stock" : null))
    : (order.supply_method_selected_at ? order.production_method : null);
  const has = (m) => (hasDec ? (summary[m] || 0) > 0 : selected === m);
  const locked = !!order.ready_purchase_request_no || (order.line_items || []).some((l) => l.production_started);
  const label = SUPPLY_LABEL[selected];
  const choose = async (m) => {
    setSaving(true);
    try {
      await api.patch(`/orders/${order.id}/supply-method`, { method: m });
      toast.success("Tedarik yöntemi kaydedildi");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Card className="bg-slate-900 border-slate-800 rounded-sm p-4" data-testid="supply-method-card">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <div className="text-xs uppercase font-mono text-slate-500">Tedarik Yöntemi</div>
        <Badge variant="outline" data-testid="supply-method-selected"
          className={`text-[10px] uppercase font-mono ${selected ? "border-emerald-800 text-emerald-300 bg-emerald-950/30" : "border-amber-800 text-amber-300 bg-amber-950/30"}`}>
          {label || "Seçilmedi"}
        </Badge>
        {order.ready_purchase_request_no && (
          <span className="text-[10px] font-mono text-slate-400" data-testid="supply-method-request-no">Talep: {order.ready_purchase_request_no}</span>
        )}
      </div>
      {hasDec && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="supply-summary">
          {["stock", "internal", "outsourced", "ready_purchase"].filter((m) => summary[m] > 0).map((m) => (
            <span key={m} data-testid={`supply-summary-${m}`}
              className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border border-slate-700 text-slate-300">
              {SUPPLY_LABEL[m]}: <span className="text-slate-50 font-semibold">{fmtQty(summary[m])}</span> adet
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-2 items-center">
        {!["mixed", "stock"].includes(selected) && SUPPLY_METHODS.map(([v, l, cls]) => (
          <button key={v} type="button" data-testid={`supply-method-${v}`}
            disabled={saving || locked || !canEdit}
            onClick={() => choose(v)}
            className={`px-3 py-1.5 text-[11px] rounded-sm border font-semibold uppercase tracking-wider transition-colors disabled:cursor-not-allowed ${
              selected === v ? cls : "border-slate-700 text-slate-400 hover:border-slate-500 disabled:opacity-50"}`}>
            {l}
          </button>
        ))}
        {(has("internal") || has("outsourced")) && (
          <div className="ml-auto flex gap-2">
            {has("internal") && (
              <Button data-testid="supply-method-start-production" size="sm" onClick={() => onStartProduction("internal")}
                className="rounded-sm text-xs font-semibold uppercase tracking-wider bg-blue-600 hover:bg-blue-500">
                <Factory className="w-3.5 h-3.5 mr-1.5" />Üretime Başla
              </Button>
            )}
            {has("outsourced") && (
              <Button data-testid="supply-method-start-outsourced" size="sm" onClick={() => onStartProduction("outsourced")}
                className="rounded-sm text-xs font-semibold uppercase tracking-wider bg-amber-600 hover:bg-amber-500">
                <Factory className="w-3.5 h-3.5 mr-1.5" />Fason Üretime Başla
              </Button>
            )}
          </div>
        )}
        {has("ready_purchase") && !order.ready_purchase_request_no && (
          <Button data-testid="supply-method-create-request" size="sm" onClick={onCreateRequest}
            className="rounded-sm text-xs font-semibold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 ml-auto">
            <ShoppingCart className="w-3.5 h-3.5 mr-1.5" />Hazır Alım Talebi Oluştur
          </Button>
        )}
      </div>
      {selected === "mixed" && (
        <div className="text-[11px] text-slate-400 mt-2" data-testid="supply-method-mixed-hint">
          Her kalem kendi tedarik yöntemiyle ilerler. Yöntemi kalem bazında Düzenle › Tedarik kolonundan değiştirin.
        </div>
      )}
      {!selected && (
        <div className="text-[11px] text-amber-300/80 mt-2" data-testid="supply-method-hint">
          Üretim veya satın alma başlatmak için önce tedarik yöntemi seçin.
        </div>
      )}
    </Card>
  );
}

function InfoField({ label, value, testId }) {
  return (
    <div>
      <div className="text-[10px] uppercase font-mono text-slate-500">{label}</div>
      <div className="text-sm text-slate-100" data-testid={testId}>{value || "—"}</div>
    </div>
  );
}

export default function OrderDetail({ mode = "sales" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const isQuotes = mode === "quotes";
  const [order, setOrder] = useState(null);
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [proformaOpen, setProformaOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [prodOpen, setProdOpen] = useState(false);
  const [prodMethod, setProdMethod] = useState("");
  const [dupOpen, setDupOpen] = useState(false);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [invOpen, setInvOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [datesOpen, setDatesOpen] = useState(false);
  const [closeTarget, setCloseTarget] = useState(null); // "iptal" | "reddedildi"
  const [infoOpen, setInfoOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [o, meta] = await Promise.all([
        api.get(`/orders/${id}`),
        api.get("/orders/meta/stages").catch(() => ({ data: { labels: {} } })),
      ]);
      setOrder(o.data);
      setLabels(meta.data?.labels || {});
      // Siparişten kesilen FT faturaları: Faturala sonrası BH durumu/gönderimi aynı sayfada
      if (o.data.stage !== "fatura" && mode !== "quotes") {
        const inv = await api.get(`/orders/${id}/invoices`).catch(() => ({ data: [] }));
        setInvoices(inv.data || []);
      } else setInvoices([]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [id, mode]);
  useEffect(() => { load(); }, [load]);

  if (loading || !order) {
    return (
      <div>
        <PageHeader title={isQuotes ? "Teklif Detayı" : "Sipariş Detayı"} subtitle="Yükleniyor..." breadcrumbs={["Ana Sayfa", isQuotes ? "Teklifler" : "Satışlar"]} />
        <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
      </div>
    );
  }

  const o = order;
  const lines = (o.line_items || []).length ? o.line_items
    : (o.product_name ? [{ id: "legacy", product_name: o.product_name, variant_sku: o.variant_sku,
        quantity: o.quantity, unit_price: o.unit_price, line_total: o.total_price, currency: o.currency,
        tax_rate: null, discount_percent: 0, discount_amount: 0,
        line_subtotal: (o.quantity || 0) * (o.unit_price || 0), line_tax_total: 0 }] : []);
  const isTeklif = o.stage === "teklif";
  const isArchivedQuote = ["iptal", "reddedildi"].includes(o.stage);
  const invoiceable = !isQuotes && o.stage === "irsaliye_fatura" && can("finance", "add");
  const listPath = isQuotes ? "/teklifler" : "/satislar";

  const openPdf = async () => {
    try {
      const resp = await api.get(`/orders/${o.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.target = "_blank"; a.rel = "noopener";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const promoteToSale = async () => {
    try {
      const la = o.line_approval;
      const started = la && (la.approved + la.rejected) > 0;
      if (started || (o.line_items || []).length > 1) {
        const { data } = await api.post(`/orders/${o.id}/convert-approved`);
        toast.success(data.mode === "full"
          ? "Teklif siparişe dönüştürüldü"
          : `Kısmi dönüşüm: ${data.order_no} oluşturuldu (${data.converted_lines}/${data.total_lines} kalem)`);
        load();
      } else {
        await api.patch(`/orders/${o.id}/stage`, { stage: "siparis_onay" });
        toast.success("Teklif siparişe dönüştürüldü");
        load();
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createPurchaseRequest = async () => {
    try {
      const { data } = await api.post(`/orders/${o.id}/purchase-request`);
      toast.success(`Satın alma talebi oluşturuldu (${data.request_no}) — ${data.items.length} kalem`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409) toast.info(String(detail));
      else if (e?.response?.status === 400) toast.warning(String(detail));
      else toast.error(formatApiError(e));
    }
  };

  const sendToBH = async (doc = o) => {
    if (doc.bh_send_status === "unverified" && !window.confirm("Önceki gönderimin sonucu doğrulanamadı. Bizim Hesap'ta bu faturanın OLMADIĞINI kontrol ettiyseniz tekrar gönderin. Devam?")) return;
    try {
      const { data } = await api.post(`/orders/${doc.id}/send-bizim-hesap${doc.bh_send_status === "unverified" ? "?force=true" : ""}`);
      toast.success(`Bizim Hesap'a gönderildi${data.bh_ref ? ` (ref: ${data.bh_ref})` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const remove = async () => {
    if (!window.confirm(`${o.order_no} silinsin mi?`)) return;
    try {
      await api.delete(`/orders/${o.id}`);
      toast.success("Kayıt silindi");
      navigate(listPath);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const subtotal = lines.reduce((s, l) => s + Number(l.line_subtotal ?? (l.quantity || 0) * (l.unit_price || 0)), 0);
  const discountTotal = lines.reduce((s, l) => s + Number(l.discount_amount || 0), 0);
  const taxTotal = lines.reduce((s, l) => s + Number(l.line_tax_total || 0), 0);

  const Btn = ({ testId, onClick, icon: Icon, label, className = "", variant }) => (
    <Button data-testid={testId} onClick={onClick} variant={variant || "outline"} size="sm"
      className={`rounded-sm text-xs font-semibold uppercase tracking-wider ${className}`}>
      <Icon className="w-3.5 h-3.5 mr-1.5" />{label}
    </Button>
  );

  return (
    <div>
      <PageHeader
        title={`${isQuotes ? "Teklif" : "Sipariş"} — ${o.order_no}`}
        subtitle={o.customer_name}
        breadcrumbs={["Ana Sayfa", isQuotes ? "Teklifler" : "Satışlar", o.order_no]}
        actions={
          <Button variant="outline" size="sm" onClick={() => navigate(listPath)} className="rounded-sm" data-testid="detail-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Listeye Dön
          </Button>
        }
      />
      <div className="p-4 lg:p-8 max-w-7xl mx-auto space-y-4" data-testid="order-detail-page">
        {/* Genel bilgiler */}
        <Card className="bg-slate-900 border-slate-800 rounded-sm p-5">
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <Badge variant="outline" className="bg-slate-950 border-slate-700 text-slate-300 uppercase text-[10px] font-mono" data-testid="detail-stage-badge">
              {labels[o.stage] || o.stage}
            </Badge>
            {o.line_approval && o.line_approval.pending > 0 && (
              <Badge variant="outline" className="bg-amber-950/30 border-amber-900/60 text-amber-300 uppercase text-[9px] font-mono" data-testid="detail-approval-badge">
                Onay Bekliyor {o.line_approval.pending}/{o.line_approval.total}
              </Badge>
            )}
            {o.bh_send_status === "sent" && <Badge variant="outline" data-testid="detail-bh-badge" title={`Bizim Hesap ref: ${o.bh_ref || "-"}`} className="bg-emerald-950/30 border-emerald-900/60 text-emerald-300 text-[11px] leading-none py-[5px] px-2.5 font-mono font-semibold">BH ✓</Badge>}
            {o.bh_send_status === "unverified" && <Badge variant="outline" data-testid="detail-bh-unverified-badge" title={o.bh_error || ""} className="bg-amber-950/30 border-amber-900/60 text-amber-300 text-[11px] leading-none py-[5px] px-2.5 font-mono font-semibold">BH ? Doğrulanamadı</Badge>}
            {o.bh_send_status === "failed" && <Badge variant="outline" data-testid="detail-bh-failed-badge" title={o.bh_error || ""} className="bg-rose-950/30 border-rose-900/60 text-rose-300 text-[11px] leading-none py-[5px] px-2.5 font-mono font-semibold">BH Hata</Badge>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoField label="Müşteri" value={o.customer_name} testId="detail-customer" />
            <InfoField label="Oluşturma Tarihi" value={fmtDate(o.created_at)} testId="detail-created" />
            {isQuotes && <InfoField label="Geçerlilik" value={fmtDate(o.expires_at)} testId="detail-expires" />}
            <InfoField label="Termin" value={fmtDate(o.due_date)} testId="detail-due" />
            <InfoField label="Öncelik" value={o.priority} testId="detail-priority" />
            <InfoField label="Satış Temsilcisi" value={o.sales_rep_name} testId="detail-rep" />
            {o.notes && <InfoField label="Notlar" value={o.notes} testId="detail-notes" />}
            {isArchivedQuote && (
              <>
                <InfoField label="Sonuçlanma Sebebi" value={o.cancel_reason || "Sebep belirtilmemiş"} testId="detail-cancel-reason" />
                {o.cancel_note && <InfoField label="Açıklama" value={o.cancel_note} testId="detail-cancel-note" />}
              </>
            )}
          </div>
        </Card>

        {/* Kalemler */}
        <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
          <div className="px-4 py-3 text-xs uppercase font-mono text-slate-500 border-b border-slate-800">Kalemler ({lines.length})</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="detail-lines-table">
              <thead className="bg-slate-950/60 border-b border-slate-800">
                <tr className="text-[10px] uppercase font-mono text-slate-500">
                  {["Beden", "Ürün", "Miktar", "Birim Fiyat", "İskonto", "KDV", "Toplam", "Para Birimi", ...(isQuotes ? ["Onay"] : [])].map((h) => (
                    <th key={h} data-testid={`detail-th-${h}`} className={`text-left px-3 py-2 whitespace-nowrap ${h === "Beden" ? "w-16 min-w-[3.5rem]" : h === "Ürün" ? "w-full" : ""}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {lines.map((l, i) => {
                  const discPct = Number(l.discount_percent || 0) + (Number(l.discount_amount || 0) > 0 && Number(l.line_subtotal || 0) > 0 ? (Number(l.discount_amount) / Number(l.line_subtotal)) * 100 : 0);
                  return (
                    <tr key={l.id || i} data-testid={`detail-line-${l.id || i}`}>
                      <td className="px-3 py-2 text-xs font-mono text-slate-200 whitespace-nowrap w-16" data-testid={`detail-line-size-${l.id || i}`}>{l.size || "—"}</td>
                      <td className="px-3 py-2 text-xs text-slate-100 w-full min-w-[16rem]">{l.product_name}</td>
                      <td className="px-3 py-2 text-xs font-mono whitespace-nowrap">{fmtQty(l.quantity)}</td>
                      <td className="px-3 py-2 text-xs font-mono whitespace-nowrap">{fmt(l.unit_price)}</td>
                      <td className="px-3 py-2 text-xs font-mono">{discPct > 0 ? `%${fmtQty(discPct)}` : "—"}</td>
                      <td className="px-3 py-2 text-xs font-mono">%{fmtQty(l.tax_rate || 0)}</td>
                      <td className="px-3 py-2 text-xs font-mono whitespace-nowrap">{fmt(l.line_total ?? l.total_price ?? (l.quantity * l.unit_price))}</td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">{CUR_SYM[l.currency || "TRY"]} {l.currency || "TRY"}</td>
                      {isQuotes && (
                        <td className="px-3 py-2 text-xs" data-testid={`detail-line-approval-${l.id || i}`}>
                          {l.approval_status ? (
                            <span className={`font-mono text-[10px] uppercase ${l.approval_status === "onaylandi" ? "text-emerald-400" : l.approval_status === "reddedildi" ? "text-rose-400" : "text-amber-400"}`}>
                              {APPROVAL_LABEL[l.approval_status] || l.approval_status}
                            </span>
                          ) : "—"}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap justify-end gap-x-6 gap-y-1 px-4 py-3 border-t border-slate-800 text-xs font-mono" data-testid="detail-totals">
            <span className="text-slate-500">Ara Toplam: <span className="text-slate-200">{fmt(subtotal)}</span></span>
            {discountTotal > 0 && <span className="text-slate-500">İskonto: <span className="text-rose-400">-{fmt(discountTotal)}</span></span>}
            <span className="text-slate-500">KDV: <span className="text-slate-200">{fmt(taxTotal)}</span></span>
            <span className="text-slate-500">Genel Toplam: <span className="text-blue-400 font-bold">{fmt(o.total_price)}</span></span>
          </div>
        </Card>

        {invoices.length > 0 && <OrderInvoicesCard invoices={invoices} onSend={sendToBH} />}

        {!isQuotes && !isTeklif && !isArchivedQuote && (
          <SupplyMethodCard order={o} canEdit={can("production", "edit") || can("production", "manage")}
            onChanged={load} onStartProduction={(m) => { setProdMethod(m); setProdOpen(true); }} onCreateRequest={createPurchaseRequest} />
        )}

        {/* İşlemler — liste ekranındaki mevcut işlemler, aynı stage/permission kurallarıyla */}
        <Card className="bg-slate-900 border-slate-800 rounded-sm p-4">
          <div className="text-xs uppercase font-mono text-slate-500 mb-3">İşlemler</div>
          <div className="flex flex-wrap gap-2" data-testid="detail-actions">
            {canOrder(can, "edit", o) && <Btn testId="detail-edit-btn" onClick={() => setEditOpen(true)} icon={Pencil} label="Düzenle" className="border-slate-700 text-slate-300 hover:bg-slate-800" />}
            <Btn testId="detail-pdf-btn" onClick={openPdf} icon={Download} label="PDF Görüntüle" className="border-blue-800 text-blue-400 hover:bg-blue-950/40" />
            <Btn testId="detail-proforma-btn" onClick={() => setProformaOpen(true)} icon={FileText} label="Proforma Oluştur" className="border-cyan-800 text-cyan-400 hover:bg-cyan-950/40" />
            <Btn testId="detail-payment-btn" onClick={() => setPayOpen(true)} icon={Wallet} label="Tahsilat Ekle" className="border-emerald-800 text-emerald-400 hover:bg-emerald-950/40" />
            {canOrder(can, "add", o) && <Btn testId="detail-duplicate-btn" onClick={() => setDupOpen(true)} icon={Copy} label="Kopyala" className="border-blue-800 text-blue-400 hover:bg-blue-950/40" />}
            {!(isQuotes && isTeklif) && (
              <Btn testId="detail-production-btn" onClick={() => setProdOpen(true)} icon={Factory} label="Reçeteden Üretim" className="border-purple-800 text-purple-400 hover:bg-purple-950/40" />
            )}
            {isQuotes && isTeklif && (
              <>
                <Btn testId="detail-purchase-request-btn" onClick={createPurchaseRequest} icon={ShoppingCart} label="Satın Alma Talebi Oluştur" className="border-amber-800 text-amber-400 hover:bg-amber-950/40" />
                <Btn testId="detail-line-approval-btn" onClick={() => setApprovalOpen(true)} icon={ListChecks} label="Kalem Onayı" className="border-cyan-800 text-cyan-400 hover:bg-cyan-950/40" />
              </>
            )}
            {isQuotes && (
              <Btn testId="detail-promote-btn" onClick={promoteToSale} icon={ArrowRight} label="Siparişe Çevir" className="border-emerald-800 text-emerald-400 hover:bg-emerald-950/40" />
            )}
            {invoiceable && (
              <Btn testId="detail-invoice-btn" onClick={() => setInvOpen(true)} icon={FileText} label="Faturala" className="border-cyan-800 text-cyan-400 hover:bg-cyan-950/40" />
            )}
            {o.stage === "fatura" && o.bh_send_status !== "sent" && can("finance", "edit") && (
              <Btn testId="detail-invoice-dates-btn" onClick={() => setDatesOpen(true)} icon={CalendarDays} label="Fatura Tarihleri" className="border-amber-800 text-amber-400 hover:bg-amber-950/40" />
            )}
            {o.stage === "fatura" && o.bh_send_status !== "sent" && (
              <Btn testId="detail-bh-send-btn" onClick={() => sendToBH()} icon={Send} label="Bizim Hesap'a Gönder" className="border-indigo-800 text-indigo-400 hover:bg-indigo-950/40" />
            )}
            {isQuotes && isTeklif && (
              <>
                <Btn testId="detail-reject-btn" onClick={() => setCloseTarget("reddedildi")} icon={XCircle} label="Reddet" className="border-amber-800 text-amber-400 hover:bg-amber-950/40" />
                <Btn testId="detail-cancel-btn" onClick={() => setCloseTarget("iptal")} icon={Ban} label="İptal Et" className="border-rose-800 text-rose-400 hover:bg-rose-950/40" />
              </>
            )}
            {isQuotes && isArchivedQuote && (
              <Btn testId="detail-close-info-btn" onClick={() => setInfoOpen(true)} icon={Info} label="Sonuçlanma Detayı" className="border-slate-700 text-slate-300 hover:bg-slate-800" />
            )}
            {!(isQuotes && isArchivedQuote) && canOrder(can, "delete", o) && (
              <Btn testId="detail-delete-btn" onClick={remove} icon={Trash2} label="Sil" className="border-rose-900 text-rose-400 hover:bg-rose-950/40" />
            )}
          </div>
        </Card>
      </div>

      <ProformaDialog order={o} open={proformaOpen} onOpenChange={setProformaOpen} />
      <EditOrderSheet order={o} open={editOpen} onOpenChange={setEditOpen} onSaved={load} />
      <PaymentDialog order={o} open={payOpen} onOpenChange={setPayOpen} onSaved={load} />
      <ProductionPlanDialog order={o} open={prodOpen} onOpenChange={setProdOpen} onDone={load} initialMethod={prodMethod} />
      <DuplicateOrderDialog order={o} open={dupOpen} onOpenChange={setDupOpen} onDuplicated={load} />
      <QuoteLineApprovalDialog order={o} open={approvalOpen} onOpenChange={setApprovalOpen} onChanged={load} />
      <InvoiceOrderDialog order={o} open={invOpen} onOpenChange={setInvOpen} onSaved={load} />
      <InvoiceDatesDialog order={datesOpen ? o : null} onClose={() => setDatesOpen(false)} onSaved={load} />
      <QuoteCloseDialog order={closeTarget ? o : null} stage={closeTarget} onClose={() => setCloseTarget(null)} onSaved={load} />
      <QuoteCloseInfoDialog order={infoOpen ? o : null} labels={labels} onClose={() => setInfoOpen(false)} />
    </div>
  );
}
